import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSiteSettings } from "./SiteSettingsContext";

const STORAGE_KEY = "pritch_cart_v1";

// Display-only fallback constants — actual values come from SiteSettingsContext
// at runtime so admin edits go live. Kept for any consumer outside the provider.
export const SHIPPING_FEE = 2.99;
export const FREE_SHIPPING_THRESHOLD = 50.0;

// Cart contents are non-sensitive (SKU + colourway + size + display price) and
// must persist across reloads so customers can build their rotation over time.
// Sensitive data (admin tokens) lives in httpOnly cookies — see AdminAuthContext.
const CartContext = createContext(null);

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.warn("[cart] failed to read localStorage", err);
    return [];
  }
};

const writeStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (err) {
    console.warn("[cart] failed to persist cart", err);
  }
};

// CartContext: cart items use a composite key that now includes grip so the
// same colourway in different grip patterns are treated as separate line items.
const itemKey = (item) =>
  item.type === "sock"
    ? `${item.type}:${item.slug}:${item.colourway}:${item.size}:${item.grip || "grid"}`
    : `${item.type}:${item.slug}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(readStorage);
  const [open, setOpen] = useState(false);
  const settings = useSiteSettings();
  const shippingFee = Number(settings?.shipping_fee ?? SHIPPING_FEE);
  const freeThreshold = Number(settings?.free_shipping_threshold ?? FREE_SHIPPING_THRESHOLD);

  useEffect(() => {
    writeStorage(items);
  }, [items]);

  const addItem = useCallback((incoming) => {
    setItems((prev) => {
      const key = itemKey(incoming);
      const idx = prev.findIndex((i) => itemKey(i) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + (incoming.quantity || 1) };
        return next;
      }
      return [...prev, { ...incoming, quantity: incoming.quantity || 1 }];
    });
    setOpen(true);
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (itemKey(i) === key ? { ...i, quantity: Math.max(1, quantity) } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => itemKey(i) !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const count = items.reduce((sum, i) => sum + i.quantity, 0);
    const qualifiesFree = subtotal >= freeThreshold;
    const shipping = subtotal === 0 || qualifiesFree ? 0 : shippingFee;
    const total = subtotal + shipping;
    const remainingForFree = Math.max(0, freeThreshold - subtotal);
    return { subtotal, count, shipping, total, qualifiesFree, remainingForFree, freeThreshold };
  }, [items, shippingFee, freeThreshold]);

  const value = useMemo(
    () => ({
      items,
      open,
      setOpen,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      totals,
      itemKey,
    }),
    [items, open, addItem, updateQuantity, removeItem, clearCart, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
